import React, { useState } from "react";
import PracticeLayout from "../../../components/PracticeLayout";
import PracticeSection from "../../../components/PracticeSection";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";
import useDocumentTitle from "../../../hooks/useDocumentTitle";

export default function LevelBasic() {
  useDocumentTitle("Level 1 · Basic — Practice");
  const [clicks, setClicks] = useState(0);
  const [disabled, setDisabled] = useState(false);
  const [checkbox1, setCheckbox1] = useState(false);
  const [checkboxes, setCheckboxes] = useState({ apple: true, banana: false, cherry: false });
  const [radio, setRadio] = useState("automation");
  const [colour, setColour] = useState("");
  const [text, setText] = useState("");
  const [email, setEmail] = useState("");
  const [num, setNum] = useState("");

  return (
    <PracticeLayout
      eyebrow="Level 1 · Basic"
      title="The seven building blocks"
      subtitle="Every automation flow uses these controls. Warm up by locating and interacting with each one."
      seo={{
        title: "Level 1 · Basic Elements — Buttons, Inputs, Checkboxes",
        description: "Practise Selenium/Playwright locators on buttons, links, inputs, checkboxes, radios and native selects — every element has a stable data-testid.",
        keywords: "selenium button practice, playwright input practice, checkbox radio practice, basic web element automation",
        image: "/og/og-basic.svg",
      }}
    >
      <PracticeSection id="text" title="Text" blurb="Different text elements — practice locating by role, tag or text.">
        <div className="space-y-2">
          <h3 data-testid="heading-h3" className="font-display font-bold text-lg">This is a heading (h3)</h3>
          <p data-testid="text-paragraph" className="text-zinc-700">This is a paragraph. Automation should be able to read the exact text content.</p>
          <p><strong data-testid="text-bold">Bold text</strong> · <em data-testid="text-italic">Italic text</em> · <code data-testid="text-code" className="tag-mono text-xs px-1 bg-zinc-100 rounded">inline code</code></p>
          <span data-testid="text-hidden" className="sr-only">This text is visually hidden but accessible.</span>
        </div>
      </PracticeSection>

      <PracticeSection id="button" title="Button" hint="Locator: [data-testid='btn-counter']">
        <div className="flex flex-wrap gap-3 items-center">
          <Button onClick={() => setClicks((c) => c + 1)} data-testid="btn-counter" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
            Click me
          </Button>
          <span data-testid="btn-counter-value" className="tag-mono text-sm">clicks: {clicks}</span>
          <Button
            variant="outline"
            onClick={() => setDisabled((d) => !d)}
            data-testid="btn-toggle-disabled"
          >
            {disabled ? "Enable target" : "Disable target"}
          </Button>
          <Button
            data-testid="btn-target"
            disabled={disabled}
            variant="secondary"
          >
            {disabled ? "I am disabled" : "I am enabled"}
          </Button>
        </div>
      </PracticeSection>

      <PracticeSection id="link" title="Link" hint="Practice getAttribute('href') and target='_blank' handling.">
        <div className="flex flex-wrap gap-3 items-center">
          <a href="/jobs" data-testid="link-internal" className="text-[#002FA7] hover:underline">Internal link (Jobs)</a>
          <a href="https://playwright.dev" target="_blank" rel="noreferrer" data-testid="link-external" className="text-[#002FA7] hover:underline">External link (opens new tab)</a>
          <a href="mailto:hello@example.com" data-testid="link-mailto" className="text-[#002FA7] hover:underline">mailto:hello@example.com</a>
          <a href="tel:+1234567890" data-testid="link-tel" className="text-[#002FA7] hover:underline">tel:+1234567890</a>
        </div>
      </PracticeSection>

      <PracticeSection id="input" title="Input" blurb="Text, number, email, password variants.">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Text</Label>
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your name" data-testid="input-text" className="mt-1.5" />
            <div className="tag-mono text-[11px] text-zinc-500 mt-1" data-testid="input-text-value">value: {text || "—"}</div>
          </div>
          <div>
            <Label>Number</Label>
            <Input type="number" value={num} onChange={(e) => setNum(e.target.value)} placeholder="Age" data-testid="input-number" className="mt-1.5" />
            <div className="tag-mono text-[11px] text-zinc-500 mt-1" data-testid="input-number-value">value: {num || "—"}</div>
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="input-email" className="mt-1.5" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" placeholder="Secret" data-testid="input-password" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Read-only</Label>
            <Input readOnly value="you can't edit me" data-testid="input-readonly" className="mt-1.5" />
          </div>
        </div>
      </PracticeSection>

      <PracticeSection id="checkbox" title="Checkbox" hint="Handle Radix's role='checkbox' via aria-checked.">
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <Checkbox checked={checkbox1} onCheckedChange={setCheckbox1} data-testid="cb-single" />
            <span className="text-sm">I agree to the terms</span>
          </label>
          <div className="space-y-2">
            {Object.keys(checkboxes).map((k) => (
              <label key={k} className="flex items-center gap-2">
                <Checkbox
                  checked={checkboxes[k]}
                  onCheckedChange={(v) => setCheckboxes((c) => ({ ...c, [k]: !!v }))}
                  data-testid={`cb-${k}`}
                />
                <span className="text-sm capitalize">{k}</span>
              </label>
            ))}
          </div>
          <label className="flex items-center gap-2 opacity-60">
            <Checkbox checked disabled data-testid="cb-disabled-checked" />
            <span className="text-sm">Pre-selected & disabled</span>
          </label>
          <div data-testid="cb-summary" className="tag-mono text-[11px] text-zinc-500">
            selected: {[checkbox1 && "terms", ...Object.entries(checkboxes).filter(([, v]) => v).map(([k]) => k)].filter(Boolean).join(", ") || "none"}
          </div>
        </div>
      </PracticeSection>

      <PracticeSection id="radio" title="Radio" hint="Only one option can be active at a time.">
        <RadioGroup value={radio} onValueChange={setRadio} data-testid="radio-group" className="space-y-2">
          {["manual", "automation", "performance", "security"].map((v) => (
            <label key={v} className="flex items-center gap-2">
              <RadioGroupItem value={v} id={`radio-${v}`} data-testid={`radio-${v}`} />
              <span className="text-sm capitalize">{v} testing</span>
            </label>
          ))}
        </RadioGroup>
        <div data-testid="radio-value" className="tag-mono text-[11px] text-zinc-500 mt-2">value: {radio}</div>
      </PracticeSection>

      <PracticeSection id="dropdown" title="Dropdown (native select)" hint="This is a native <select> — use selectOption().">
        <div className="max-w-sm">
          <Label>Favourite colour</Label>
          <select
            value={colour}
            onChange={(e) => setColour(e.target.value)}
            data-testid="select-colour"
            className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
          >
            <option value="">— choose —</option>
            <option value="red">Red</option>
            <option value="green">Green</option>
            <option value="blue">Blue</option>
            <option value="yellow">Yellow</option>
          </select>
          <div data-testid="select-colour-value" className="tag-mono text-[11px] text-zinc-500 mt-1">value: {colour || "—"}</div>
        </div>
      </PracticeSection>
    </PracticeLayout>
  );
}
